import React, { useState } from "react";
import {
  Button,
  Grid,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  Divider,
  Stack,
  Box,
  useMediaQuery,
} from "@mui/material";
import Layout from "../../components/LayoutWithHeader.js";
import GridViewIcon from "@mui/icons-material/GridView";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useAuth } from "../../context/authContext.js";
import useApi from "../../hooks/useApi.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getContentOptions,
  getContentOptionsArray,
  getSortByOptions,
  getStatusOptions,
} from "../../utils/utils.js";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useTheme } from "@emotion/react";
import { useThemeContext } from "../../context/themeContext.js";
import { toast } from "react-toastify";
import { updateProfileInCache } from "../../utils/cacheUtils.js";
import { useNavigate, useSearchParams } from "react-router-dom";

// Shared caption under a preference's control
const captionSx = { display: "block", lineHeight: 1.3, mt: 1 };

/**
 * Theme and primary interest for everyone (the second onboarding step for a
 * new user), plus Track page defaults once onboarded. Changes are held locally
 * and saved together.
 */
const SettingsPreferences = () => {
  // Hooks
  const theme = useTheme();
  const { user, updateUser } = useAuth();
  const { editProfile } = useApi();
  const { setMode } = useThemeContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // New user
  const [searchParams] = useSearchParams();
  const newUser = searchParams.get("newUser") === "true";

  // Utils
  const statusOptions = getStatusOptions(theme);

  // Preferences
  const [darkModePref, setDarkModePref] = useState(user.preferences.darkMode);
  const [statusPref, setStatusPref] = useState(user.preferences.status);
  const [sortByPref, setSortByPref] = useState(
    user.preferences.sortBy["finished"],
  );
  const [contentPref, setContentPref] = useState(user.preferences.content);
  const [viewPref, setViewPref] = useState(user.preferences.view);
  const [trackContentPref, setTrackContentPref] = useState(
    user.preferences.trackContent,
  );

  // Track's content type choices: All, or the primary interest picked above
  // (shown as that type, so it's clear what it means)
  const allOption = getContentOptionsArray({ showEverythingOption: true }).find(
    (option) => option.value === "all",
  );
  const primaryOption = getContentOptions()[contentPref];

  // Preference handling
  const handleDarkModePref = (event, darkModeValue) => {
    if (darkModeValue !== null) {
      const mode = darkModeValue ? "dark" : "light";
      setMode(mode);
      setDarkModePref(darkModeValue);
    }
  };

  const handleViewPref = (event, value) => {
    if (value !== null) {
      setViewPref(value);
    }
  };

  const handleStatusPref = (event, value) => {
    if (value !== null) {
      setStatusPref(value);
    }
  };

  const handleSortByPref = (event) => {
    setSortByPref(event.target.value);
  };

  const handleContentPref = (event, newValue) => {
    if (newValue !== null) {
      setContentPref(newValue);
    }
  };

  const handleTrackContentPref = (event, value) => {
    if (value !== null) {
      setTrackContentPref(value);
    }
  };

  // Mutation handling
  const hasUnsavedChanges =
    darkModePref !== user.preferences.darkMode ||
    statusPref !== user.preferences.status ||
    sortByPref !== user.preferences.sortBy["finished"] ||
    contentPref !== user.preferences.content ||
    viewPref !== user.preferences.view ||
    trackContentPref !== user.preferences.trackContent;

  const editProfileMutation = useMutation({
    mutationFn: editProfile,
    onSuccess: (data) => {
      updateProfileInCache({
        queryClient,
        newUserDetails: data,
        onUserUpdate: updateUser,
      });
      if (newUser) navigate("/settings/services?newUser=true");
    },
  });

  const handleSave = () => {
    editProfileMutation.mutate({
      darkModePref,
      statusPref,
      contentPref,
      viewPref,
      sortByPref,
      trackContentPref,
    });
    if (!newUser) toast.success("Your preferences have been saved");
  };

  return (
    <Layout
      title={newUser ? "Set your preferences" : "Curate preferences"}
      showBackButton={!newUser}
      showMenu={!newUser}
    >
      <Grid container spacing={3}>
        <Grid item xs={3}>
          <Typography
            variant="body2"
            sx={{ textAlign: "right", textWrap: "balance" }}
          >
            Default <br /> theme
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <ToggleButtonGroup
            value={darkModePref}
            exclusive
            size="small"
            fullWidth
            onChange={handleDarkModePref}
            aria-label="change dark mode preference"
          >
            <ToggleButton value={false} aria-label="light mode">
              <Stack direction="row" spacing={1} alignItems="center">
                <LightModeIcon />
                <Typography variant="inherit">Light</Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton value={true} aria-label="dark mode">
              <Stack direction="row" spacing={1} alignItems="center">
                <DarkModeIcon />
                <Typography variant="inherit">Dark</Typography>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={3}>
          <Typography
            variant="body2"
            sx={{ textAlign: "right", textWrap: "balance" }}
          >
            Primary <br /> interest
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <ToggleButtonGroup
            value={contentPref}
            onChange={handleContentPref}
            fullWidth
            exclusive
            size="small"
          >
            {getContentOptionsArray().map(
              ({ value, icon, labelPlural, disabled }) => (
                <ToggleButton key={value} value={value} disabled={disabled}>
                  {icon}
                </ToggleButton>
              ),
            )}
          </ToggleButtonGroup>
          <Typography variant="caption" sx={captionSx}>
            What do you track most often? {isMobile && <br />}
            Movies, TV shows or books <br />
            You can still track everything - this just sets your default
          </Typography>
        </Grid>

        {!newUser && (
          <>
            <Grid item xs={12}>
              <Divider />
              <Typography variant="body1" sx={{ mt: 1.5 }}>
                Track page defaults
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={captionSx}>
                Filter, sort and view changes are remembered while you browse.
                To go back to these defaults at any point, tap Track in the
                sidebar while you're on your Track page.
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Typography
                variant="body2"
                sx={{ textAlign: "right", textWrap: "balance" }}
              >
                Show
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <ToggleButtonGroup
                value={trackContentPref}
                exclusive
                size="small"
                fullWidth
                onChange={handleTrackContentPref}
                aria-label="change default Track content type"
              >
                <ToggleButton value="all" aria-label="all content types">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {allOption?.icon}
                    <Typography variant="inherit">
                      {allOption?.labelPlural}
                    </Typography>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="primary" aria-label="primary interest">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {primaryOption?.icon}
                    <Typography variant="inherit">
                      {primaryOption?.labelPlural}
                    </Typography>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" sx={captionSx}>
                Everything, or just your primary interest from above
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Typography variant="body2" sx={{ textAlign: "right" }}>
                Status
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <ToggleButtonGroup
                value={statusPref}
                exclusive
                size="small"
                fullWidth
                onChange={handleStatusPref}
                aria-label="change default Track status"
              >
                <ToggleButton value="queued" aria-label="open on queued">
                  {statusOptions.queued.icon}
                </ToggleButton>
                <ToggleButton value="started" aria-label="open on started">
                  {statusOptions.started.icon}
                </ToggleButton>
                <ToggleButton value="finished" aria-label="open on finished">
                  {statusOptions.finished.icon}
                </ToggleButton>
              </ToggleButtonGroup>
              {/* Icons alone, as three labelled buttons don't fit a phone */}
              <Typography variant="caption" sx={captionSx}>
                {statusOptions.queued.label} · {statusOptions.started.label} ·{" "}
                {statusOptions.finished.label}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                variant="body2"
                sx={{ textAlign: "right", textWrap: "balance" }}
              >
                View
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <ToggleButtonGroup
                value={viewPref}
                exclusive
                size="small"
                fullWidth
                onChange={handleViewPref}
                aria-label="change default view preference"
              >
                <ToggleButton value={"grid"} aria-label="grid view">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GridViewIcon />
                    <Typography variant="inherit">Grid</Typography>
                  </Stack>
                </ToggleButton>
                <ToggleButton value={"list"} aria-label="list view">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormatListBulletedIcon />
                    <Typography variant="inherit">List</Typography>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={3}>
              <Typography
                variant="body2"
                sx={{ textAlign: "right", textWrap: "balance" }}
              >
                Sort by
              </Typography>
            </Grid>
            <Grid item xs={9}>
              {/* A Select rather than a ToggleButtonGroup here — five options
                  no longer fit comfortably in one row on mobile. */}
              <Select
                value={sortByPref}
                onChange={handleSortByPref}
                size="small"
                fullWidth
                aria-label="change default sort by preference"
              >
                {getSortByOptions("finished").map(({ value, label, icon }) => (
                  <MenuItem key={value} value={value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {icon}
                      <Typography variant="inherit">{label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" sx={captionSx}>
                For finished, other statuses default to log date
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            (!newUser && !hasUnsavedChanges) || editProfileMutation.isPending
          }
        >
          {newUser ? "Continue" : hasUnsavedChanges ? "Save" : "All saved"}
        </Button>
      </Box>
    </Layout>
  );
};

export default SettingsPreferences;
